/**
 * PureRichText Visualization Options Schema
 *
 * Defines the JSON schema for PureRichText visualization options.
 */

import { COLOR_OR_TOKEN_PATTERN_WITH_RGBA, getPattern } from '../utils';
import type { OptionsSchema } from '../types';

/**
 * PureRichText options schema with all required properties
 */
export const pureRichTextOptionsSchema: OptionsSchema = {
    // Container styling
    backgroundColor: {
        description: 'Specify the background color using a Hex code such as "#FF0000" or "transparent".',
        type: 'string',
        default: 'transparent',
        pattern: COLOR_OR_TOKEN_PATTERN_WITH_RGBA,
    },

    // Font styling
    fontColor: {
        description:
            'Specify the text color using Hex codes or RGBA values, such as "#FF0000" or "rgba(25,12,13,0.1)". The default for enterprise light is "#3c444d". The default for enterprise dark is "#FFFFFF". The default for prisma dark is "rgba(255, 255, 255, 0.7)".',
        type: 'string',
        default: '> themes.defaultFontColor',
        pattern: COLOR_OR_TOKEN_PATTERN_WITH_RGBA,
    },

    highlightColor: {
        description:
            'Specify the highlight/background color for selected text using Hex codes or RGBA values.',
        type: 'string',
        default: 'transparent',
        pattern: COLOR_OR_TOKEN_PATTERN_WITH_RGBA,
    },

    fontFamily: {
        description:
            'Specify the font family for rich text content, such as "Arial" or "Times New Roman". Note: the font needs to be available to your end user.',
        type: 'string',
        default: 'Splunk Data Sans',
        pattern: getPattern([
            'Splunk Data Sans',
            'Splunk Platform Sans Mono',
            'Arial',
            'Helvetica',
            'Times New Roman',
            'Comic Sans MS',
            'Georgia',
            'Verdana',
            'Courier New',
        ]),
    },

    fontSize: {
        description:
            'Specify the base font size preset. Font sizes range from extra small (10px) to extra large (20px) with the default being 14px.',
        type: 'string',
        default: 'default',
        pattern: getPattern(['extraSmall', 'small', 'default', 'large', 'extraLarge', 'custom']),
    },

    customFontSize: {
        description:
            'Specify the custom font size in pixels when fontSize is set to "custom". Valid range is 1-200.',
        type: 'number',
        default: 14,
        minimum: 1,
        maximum: 200,
    },

    // Text formatting
    bold: {
        description: 'Apply bold font weight to all text by default.',
        type: 'boolean',
        default: false,
    },

    italic: {
        description: 'Apply italic font style to all text by default.',
        type: 'boolean',
        default: false,
    },

    underline: {
        description: 'Apply underline text decoration to all text by default.',
        type: 'boolean',
        default: false,
    },

    strikethrough: {
        description: 'Apply strikethrough text decoration to all text by default.',
        type: 'boolean',
        default: false,
    },

    // List type
    listType: {
        description: 'Specify the default list type for the content.',
        type: 'string',
        default: '',
        pattern: getPattern(['', 'unordered', 'ordered']),
    },

    // Alignment
    horizontalAlignment: {
        description: 'Specify the horizontal text alignment.',
        type: 'string',
        default: 'left',
        pattern: getPattern(['left', 'center', 'right', 'justify']),
    },

    verticalAlign: {
        description: 'Specify the vertical alignment of content within the container.',
        type: 'string',
        default: 'top',
        pattern: getPattern(['top', 'middle', 'bottom']),
    },

    // Line height
    lineHeight: {
        description:
            'Specify the line height as a number (e.g., 1.5 means 150% of font size) or string value.',
        type: ['number', 'string'],
        default: '1.5',
    },

    // Link
    link: {
        description: 'Specify a URL to make the entire rich text content a clickable link.',
        type: 'string',
        default: '',
    },

    // Border configuration
    border: {
        description: 'Border configuration for the rich text container.',
        type: 'object',
        properties: {
            color: {
                description: 'Border color using Hex codes or RGBA values.',
                type: 'string',
                pattern: COLOR_OR_TOKEN_PATTERN_WITH_RGBA,
            },
            width: {
                description: 'Border width in pixels.',
                type: 'number',
                default: 0,
                minimum: 0,
                maximum: 20,
            },
            style: {
                description: 'Border style.',
                type: 'string',
                default: 'solid',
                pattern: getPattern(['solid', 'dashed', 'dotted', 'none']),
            },
            radius: {
                description: 'Border radius (corner radius) in pixels.',
                type: 'number',
                default: 0,
                minimum: 0,
                maximum: 100,
            },
        },
    },

    // Padding
    padding: {
        description:
            'Padding in pixels. Can be a single number (all sides), array of 2 (vertical, horizontal), or array of 4 (top, right, bottom, left).',
        type: ['number', 'array'],
        default: 8,
    },

    // Rich text content (HTML)
    richTextContent: {
        description:
            'HTML content for the rich text editor. Inline styles should only include differences from default text styles.',
        type: 'string',
        default: '<p></p>',
    },
};

/**
 * Platform RichText wrapper options schema
 * Same as pureRichTextOptionsSchema
 */
export const richTextOptionsSchema = pureRichTextOptionsSchema;
